import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "../AuthProvider";
import { getApiToken } from "../authUtils";
import urlconfig from "../config/urlconfig";
import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Button,
  LinearProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { catalogTokens, parseApiError } from "./VendorCatalogTheme";
import { PdfDropzone } from "./VendorCatalogUpload";

// Swaps ONLY a catalog's separate price-list PDF,
const VendorCatalogReplacePriceListDialog = ({ catalog, open, onClose, onSuccess }) => {
  const { instance } = useMsal();
  const { account } = useAuth();

  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!catalog) return null;

  const isAdding = !catalog.hasSeparatePriceList;

  const handleSelect = (selected) => {
    if (!selected) return;
    const isPdf = selected.type === "application/pdf" || selected.name?.toLowerCase().endsWith(".pdf");
    if (!isPdf) return setFileError("Only PDF files are allowed");
    if (selected.size > 20 * 1024 * 1024) return setFileError("File exceeds 20MB limit");
    setFile(selected);
    setFileError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    setFile(null);
    setFileError(null);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!file) return;
    setSubmitting(true);
    setError(null);

    try {
      const token = await getApiToken(instance, account);
      const authHeaders = { Authorization: `Bearer ${token}` };

      // Fetch a fresh signed URL for the current catalog PDF, then pull its bytes so they can be
      // resubmitted unchanged — /replace has no "keep the existing file" option.
      const urlRes = await fetch(
        `${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs/${catalog.id}/download-url`,
        { headers: authHeaders }
      );
      if (!urlRes.ok) throw new Error(await parseApiError(urlRes));
      const { url } = await urlRes.json();

      const pdfRes = await fetch(url);
      if (!pdfRes.ok) throw new Error("Could not fetch the current catalog PDF");
      const pdfBlob = await pdfRes.blob();

      const formData = new FormData();
      (catalog.categories || []).forEach((c) => formData.append("categoryNames", c.name));
      (catalog.brands || []).forEach((b) => formData.append("brandNames", b.name));
      formData.append(
        "subCategoriesJson",
        JSON.stringify((catalog.subCategories || []).map((sc) => ({ category: sc.categoryName, name: sc.name })))
      );
      (catalog.collections || []).forEach((c) => formData.append("collectionIds", c.id));
      formData.append("minPrice", catalog.minPrice);
      formData.append("maxPrice", catalog.maxPrice);
      formData.append("file", pdfBlob, "catalog.pdf");
      formData.append("priceListFile", file);
      if (catalog.remarks && catalog.remarks.trim()) {
        formData.append("remarks", catalog.remarks.trim());
      }

      const res = await fetch(
        `${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs/${catalog.id}/replace`,
        { method: "POST", headers: authHeaders, body: formData }
      );
      if (!res.ok) throw new Error(await parseApiError(res));
      const saved = await res.json();

      setFile(null);
      onSuccess?.(saved);
    } catch (err) {
      setError(err.message || `Could not ${isAdding ? "add" : "replace"} the price list`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1, pr: 1 }}>
        <IconButton size="small" onClick={handleClose} disabled={submitting} sx={{ color: catalogTokens.color.textSecondary }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 0, pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={800} sx={{ color: catalogTokens.color.textPrimary, mb: 0.25 }}>
          {isAdding ? "Add price list" : "Update / Replace price list"}
        </Typography>
        <Typography variant="body2" sx={{ color: catalogTokens.color.textSecondary, mb: 2.5 }}>
          {isAdding
            ? "This catalog doesn't have a separate price list yet. Attach one as its own PDF — the catalog PDF and its tags stay as they are."
            : "Attach the vendor's updated price list PDF — the catalog PDF and its tags stay as they are."}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: `${catalogTokens.radius.md}px` }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <PdfDropzone
          file={file}
          onSelect={handleSelect}
          onRemove={() => setFile(null)}
          label="Drag & drop the price list PDF here"
          maxSizeLabel="20MB"
        />
        {fileError && (
          <Typography variant="caption" sx={{ color: "#dc2626", display: "block", mt: 0.5 }}>{fileError}</Typography>
        )}

        {submitting && <LinearProgress sx={{ borderRadius: 1, mt: 2 }} />}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSubmit}
          disabled={submitting || !file}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: `${catalogTokens.radius.md}px`,
            bgcolor: catalogTokens.color.primary,
            "&:hover": { bgcolor: catalogTokens.color.primaryDark },
          }}
        >
          {submitting ? (isAdding ? "Adding…" : "Replacing…") : isAdding ? "Add price list" : "Replace price list"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VendorCatalogReplacePriceListDialog;
